import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { format, parseISO } from 'date-fns';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { formatCurrency } from '../../../../shared/utils/currency';
import type { Deduction } from '../../types/potDetails.types';
import { CancelDeductionDialog } from './CancelDeductionDialog';

const statusConfig: Record<
  string,
  { color: 'warning' | 'success' | 'error' | 'default'; label: string }
> = {
  Booked: { color: 'warning', label: 'Pending' },
  Approved: { color: 'success', label: 'Approved' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

interface DeductionDrawerContentProps {
  deduction: Deduction;
  onCancelled?: (message: string) => void;
}

export function DeductionDrawerContent({
  deduction,
  onCancelled,
}: DeductionDrawerContentProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const status = statusConfig[deduction.status] ?? {
    color: 'default' as const,
    label: deduction.status,
  };
  const createdDate = parseISO(deduction.createdAt);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <RemoveCircleOutlineIcon
                sx={{ fontSize: 40, color: 'error.main' }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6">{deduction.description}</Typography>
                {deduction.uniqueReference && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {deduction.uniqueReference}
                  </Typography>
                )}
              </Box>
            </Box>

            <Stack spacing={1}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={status.label}
                  color={status.color}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Amount
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="error.main"
                >
                  {formatCurrency(deduction.amount)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {format(createdDate, 'MMM d, yyyy')}
                </Typography>
              </Box>
              {deduction.siteName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Site
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {deduction.siteName}
                  </Typography>
                </Box>
              )}
              {deduction.plotReference && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Plot
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {deduction.plotReference}
                  </Typography>
                </Box>
              )}
              {!deduction.siteId && !deduction.plotId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Scope
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    Organisation-wide
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {deduction.canCancel && (
          <PermissionGate permission={Permissions.Deductions.Approve}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              data-testid="cancel-deduction-button"
            >
              Cancel Deduction
            </Button>
          </PermissionGate>
        )}
      </Stack>

      <CancelDeductionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        deduction={{
          id: deduction.id,
          uniqueReference: deduction.uniqueReference,
          description: deduction.description,
          amount: deduction.amount,
          status: deduction.status,
        }}
        onSuccess={message => {
          setCancelDialogOpen(false);
          onCancelled?.(message);
        }}
      />
    </Box>
  );
}
